import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Wordmark } from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { CommandPalette } from "@/components/CommandPalette";
import { AIChat } from "@/components/AIChat";
import { Button } from "@/components/ui/button";
import { LayoutDashboard, BookOpen, ListChecks, CalendarDays, Timer, BarChart3, User, Settings as SettingsIcon, Search, Sparkles, Sun, Moon, LogOut, Bell } from "lucide-react";

const nav = [
    { to: "/app", end: true, label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
    { to: "/app/courses", label: "Courses", icon: BookOpen, testid: "nav-courses" },
    { to: "/app/assignments", label: "Assignments", icon: ListChecks, testid: "nav-assignments" },
    { to: "/app/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar" },
    { to: "/app/focus", label: "Focus", icon: Timer, testid: "nav-focus" },
    { to: "/app/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
];

export default function AppShell() {
    const [cmdOpen, setCmdOpen] = useState(false);
    const [aiOpen, setAiOpen] = useState(false);
    const { user, logout } = useAuth();
    const { theme, toggle } = useTheme();
    const navigate = useNavigate();

    useEffect(() => {
        const onKey = (e) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
                e.preventDefault();
                setCmdOpen(o => !o);
            }
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "j") {
                e.preventDefault();
                setAiOpen(o => !o);
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    return (
        <div className="min-h-screen flex bg-background">
            {/* Sidebar */}
            <aside className="hidden lg:flex flex-col w-64 border-r border-border/60 bg-card/40 p-5" data-testid="sidebar">
                <div className="mb-8"><Wordmark /></div>

                <button
                    onClick={() => setCmdOpen(true)}
                    className="flex items-center gap-2 w-full px-3 py-2 mb-6 rounded-lg border border-border/60 bg-background/60 hover:bg-muted/50 transition-colors text-sm text-muted-foreground"
                    data-testid="open-command-palette"
                >
                    <Search className="w-4 h-4" />
                    <span className="flex-1 text-left">Search or jump…</span>
                    <span className="kbd">⌘K</span>
                </button>

                <nav className="flex flex-col gap-1">
                    {nav.map(({ to, label, icon: Icon, end, testid }) => (
                        <NavLink
                            key={to}
                            to={to}
                            end={end}
                            data-testid={testid}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                                    isActive
                                        ? "bg-primary text-primary-foreground shadow-sm"
                                        : "text-foreground/80 hover:bg-muted hover:text-foreground"
                                }`
                            }
                        >
                            <Icon className="w-[18px] h-[18px]" />
                            <span className="font-medium">{label}</span>
                        </NavLink>
                    ))}
                </nav>

                <div className="mt-6 pt-6 border-t border-border/60 flex flex-col gap-1">
                    <NavLink to="/app/profile" data-testid="nav-profile" className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${isActive ? "bg-muted text-foreground" : "text-foreground/80 hover:bg-muted"}`}>
                        <User className="w-[18px] h-[18px]" /><span>Profile</span>
                    </NavLink>
                    <NavLink to="/app/settings" data-testid="nav-settings" className={({ isActive }) => `flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${isActive ? "bg-muted text-foreground" : "text-foreground/80 hover:bg-muted"}`}>
                        <SettingsIcon className="w-[18px] h-[18px]" /><span>Settings</span>
                    </NavLink>
                </div>

                <div className="mt-auto pt-6">
                    <div className="p-3 rounded-xl bg-primary/10 border border-primary/20 mb-3">
                        <div className="mono-label mb-1">AI Companion</div>
                        <p className="text-xs text-foreground/80 mb-2 leading-relaxed">Ask, summarize, flashcards.</p>
                        <Button variant="secondary" size="sm" className="w-full h-8 gap-1.5" onClick={() => setAiOpen(true)} data-testid="open-ai-chat">
                            <Sparkles className="w-3.5 h-3.5" /> Open Assistant
                        </Button>
                    </div>

                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-accent/30 flex items-center justify-center text-xs font-semibold" data-testid="user-avatar">
                            {(user?.name || "S")[0]?.toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium truncate" data-testid="user-name">{user?.name}</div>
                            <div className="text-[11px] text-muted-foreground truncate">{user?.email}</div>
                        </div>
                        <button onClick={toggle} data-testid="toggle-theme" className="p-1.5 rounded-md hover:bg-muted transition">
                            {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                        </button>
                        <button onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-btn" className="p-1.5 rounded-md hover:bg-muted transition">
                            <LogOut className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </aside>

            {/* Mobile top bar */}
            <div className="lg:hidden fixed top-0 inset-x-0 z-40 h-14 border-b border-border/60 bg-background/80 backdrop-blur px-4 flex items-center justify-between">
                <Wordmark />
                <div className="flex items-center gap-1">
                    <button onClick={() => setCmdOpen(true)} className="p-2 rounded-md hover:bg-muted" data-testid="mobile-search"><Search className="w-4 h-4" /></button>
                    <button onClick={toggle} className="p-2 rounded-md hover:bg-muted">{theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}</button>
                </div>
            </div>

            {/* Mobile bottom nav */}
            <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 h-16 border-t border-border/60 bg-background/90 backdrop-blur flex items-center justify-around px-2">
                {nav.slice(0, 5).map(({ to, label, icon: Icon, end, testid }) => (
                    <NavLink key={to} to={to} end={end} data-testid={`m-${testid}`} className={({ isActive }) => `flex flex-col items-center gap-1 px-2 py-1 rounded-md text-[10px] ${isActive ? "text-primary" : "text-muted-foreground"}`}>
                        <Icon className="w-[18px] h-[18px]" /><span>{label}</span>
                    </NavLink>
                ))}
            </nav>

            {/* Main content */}
            <main className="flex-1 min-w-0 pt-14 lg:pt-0 pb-20 lg:pb-0">
                <div className="max-w-[1400px] mx-auto p-6 md:p-8 lg:p-10 animate-fade-up">
                    <Outlet />
                </div>
            </main>

            <CommandPalette open={cmdOpen} setOpen={setCmdOpen} onAI={() => { setCmdOpen(false); setAiOpen(true); }} />
            <AIChat open={aiOpen} setOpen={setAiOpen} />
        </div>
    );
}
